import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useActiveVehicleTypes } from '@/hooks/useVehicleTypes';

export interface VehicleFormData {
  plate: string;
  renavam?: string;
  vin?: string;
  make?: string;
  model?: string;
  year: number;
  category: string;
  vehicle_type_id?: string;
  fuel_type: string;
  odometer: number;
  cost_center_id?: string;
  status: string;
}

interface VehicleFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  vehicle?: VehicleFormData & { id: string };
  onSubmit: (data: VehicleFormData) => void;
  isLoading?: boolean;
  costCenters?: Array<{ id: string; name: string; code?: string }>;
}

export function VehicleFormSheet({
  open,
  onOpenChange,
  vehicle,
  onSubmit,
  isLoading = false,
  costCenters = [],
}: VehicleFormSheetProps) {
  const { data: vehicleTypes = [], isLoading: loadingTypes } = useActiveVehicleTypes();
  
  const [formData, setFormData] = useState<VehicleFormData>({
    plate: '',
    renavam: '',
    vin: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    category: vehicleTypes[0]?.category || '',
    vehicle_type_id: vehicleTypes[0]?.id || '',
    fuel_type: 'gasoline',
    odometer: 0,
    cost_center_id: 'none',
    status: 'active'
  });

  useEffect(() => {
    if (vehicle) {
      setFormData({
        plate: vehicle.plate,
        renavam: vehicle.renavam || '',
        vin: vehicle.vin || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        category: vehicle.category,
        vehicle_type_id: vehicle.vehicle_type_id || '',
        fuel_type: vehicle.fuel_type,
        odometer: vehicle.odometer,
        cost_center_id: vehicle.cost_center_id || 'none',
        status: vehicle.status
      });
    } else {
      setFormData({
        plate: '',
        renavam: '',
        vin: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        category: vehicleTypes[0]?.category || '',
        vehicle_type_id: vehicleTypes[0]?.id || '',
        fuel_type: 'gasoline',
        odometer: 0,
        cost_center_id: 'none',
        status: 'active'
      });
    }
  }, [vehicle, open, vehicleTypes]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!formData.plate.trim()) {
      toast.error('A placa do veículo é obrigatória');
      return;
    }
    
    const plateRegex = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;
    if (!plateRegex.test(formData.plate)) {
      toast.error('Formato de placa inválido. Use o formato ABC1234 ou ABC1D23 (Mercosul)');
      return;
    }
    
    if (!formData.vehicle_type_id) {
      toast.error('Selecione o tipo de veículo');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[650px] p-6">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle>
            {vehicle ? 'Editar' : 'Novo'} Veículo
          </SheetTitle>
          <SheetDescription>
            Preencha as informações do veículo para adicionar à frota
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-medium text-sm border-b pb-2">Identificação</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plate">Placa *</Label>
                  <Input
                    id="plate"
                    value={formData.plate}
                    onChange={(e) => setFormData({...formData, plate: e.target.value.toUpperCase()})}
                    placeholder="ABC1234"
                    className="uppercase"
                    disabled={isLoading}
                    maxLength={7}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="renavam">RENAVAM</Label>
                  <Input
                    id="renavam"
                    value={formData.renavam}
                    onChange={(e) => setFormData({...formData, renavam: e.target.value})}
                    placeholder="12345678901"
                    disabled={isLoading}
                    maxLength={11}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vin">Chassi (VIN)</Label>
                <Input
                  id="vin"
                  value={formData.vin}
                  onChange={(e) => setFormData({...formData, vin: e.target.value.toUpperCase()})}
                  placeholder="9BWZZZ377VT004251"
                  className="uppercase"
                  disabled={isLoading}
                  maxLength={17}
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm border-b pb-2">Especificações</h3>
              
              {/* Grid 3 colunas: Marca, Modelo, Ano */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="make">Marca</Label>
                  <Input
                    id="make"
                    value={formData.make}
                    onChange={(e) => setFormData({...formData, make: e.target.value})}
                    placeholder="Ford"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="model">Modelo</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    placeholder="Transit"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="year">Ano</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: parseInt(e.target.value) || new Date().getFullYear()})}
                    min="1950"
                    max={new Date().getFullYear() + 1}
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Tipo de Veículo - largura total */}
              <div className="space-y-2">
                <Label htmlFor="category">Tipo de Veículo *</Label>
                <Select
                  value={formData.vehicle_type_id}
                  onValueChange={(value) => {
                    const selectedType = vehicleTypes.find(t => t.id === value);
                    setFormData({
                      ...formData,
                      vehicle_type_id: value,
                      category: selectedType?.category || ''
                    });
                  }}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de veículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingTypes ? (
                      <div className="flex items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span className="text-sm text-muted-foreground">Carregando tipos...</span>
                      </div>
                    ) : vehicleTypes.length === 0 ? (
                      <div className="py-2 px-3 text-sm text-muted-foreground">
                        Nenhum tipo de veículo cadastrado
                      </div>
                    ) : (
                      vehicleTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Grid 2 colunas: Combustível, Odômetro */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fuel_type">Combustível</Label>
                  <Select
                    value={formData.fuel_type}
                    onValueChange={(value) => setFormData({...formData, fuel_type: value})}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gasoline">Gasolina</SelectItem>
                      <SelectItem value="diesel">Diesel</SelectItem>
                      <SelectItem value="ethanol">Etanol</SelectItem>
                      <SelectItem value="flex">Flex</SelectItem>
                      <SelectItem value="electric">Elétrico</SelectItem>
                      <SelectItem value="hybrid">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="odometer">Odômetro (km)</Label>
                  <Input
                    id="odometer"
                    type="number"
                    value={formData.odometer}
                    onChange={(e) => setFormData({...formData, odometer: parseInt(e.target.value) || 0})}
                    placeholder="0"
                    min="0"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm border-b pb-2">Organização</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cost_center">Centro de Custo</Label>
                  <Select
                    value={formData.cost_center_id}
                    onValueChange={(value) => setFormData({...formData, cost_center_id: value})}
                    disabled={isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {costCenters?.map((cc) => (
                        <SelectItem key={cc.id} value={cc.id}>
                          {cc.name}{cc.code ? ` (${cc.code})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {vehicle && (
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({...formData, status: value})}
                      disabled={isLoading}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Ativo</SelectItem>
                        <SelectItem value="maintenance">Manutenção</SelectItem>
                        <SelectItem value="inactive">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </form>
        </ScrollArea>

        <SheetFooter className="py-4 border-t">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {vehicle ? 'Atualizar' : 'Criar'} Veículo
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
